/// <reference lib="dom" />
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import type { User, Permission } from 'shared/types.js';
import { api } from './api.js';
import { PasswordRequirements, validatePassword } from './common.js';

const UserDetailModal = ({ isOpen, onClose, user, onSave }: { isOpen: boolean, onClose: () => void, user: User | null, onSave: (id: number, data: { name: string, username: string, password?: string }) => void }) => {
    const [name, setName] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            const count = parseInt(document.body.dataset.modalOpenCount || '0', 10);
            if (count === 0) {
                document.body.classList.add('modal-open');
            }
            document.body.dataset.modalOpenCount = String(count + 1);

            return () => {
                const count = parseInt(document.body.dataset.modalOpenCount || '0', 10);
                const newCount = Math.max(0, count - 1);
                if (newCount === 0) {
                    document.body.classList.remove('modal-open');
                }
                document.body.dataset.modalOpenCount = String(newCount);
            };
        }
    }, [isOpen]);

    useEffect(() => {
        if (user) {
            setName(user.name);
            setUsername(user.username);
            setPassword('');
            setConfirmPassword('');
            setError('');
        }
    }, [user]);

    const handleSave = () => {
        if (!user) return;
        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        if (password) {
            const { hasMinLength, hasNumber, hasUpperCase } = validatePassword(password);
            if (!hasMinLength || !hasNumber || !hasUpperCase) {
                setError('Password does not meet the requirements.');
                return;
            }
        }

        setError('');
        const data: { name: string, username: string, password?: string } = { name, username };
        if (password) {
            data.password = password;
        }
        onSave(user.id, data);
    };

    if (!isOpen || !user) return null;

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal" style={{ width: '500px' }} onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <strong>Edit User Details</strong>
                    <button onClick={onClose}>&times;</button>
                </div>
                <div className="modal-body permissions">
                     <form className="login-form" onSubmit={e => e.preventDefault()}>
                        <div className="form-group">
                            <label htmlFor="edit-name">First Name</label>
                            <input id="edit-name" type="text" value={name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.currentTarget.value)} required />
                        </div>
                        <div className="form-group">
                            <label htmlFor="edit-username">Username</label>
                            <input id="edit-username" type="text" value={username} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUsername(e.currentTarget.value)} required />
                        </div>
                        <div className="form-group">
                            <label htmlFor="edit-password">New Password (optional)</label>
                            <input id="edit-password" type="password" value={password} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.currentTarget.value)} />
                            {password && <PasswordRequirements password={password} />}
                        </div>
                        <div className="form-group">
                            <label htmlFor="edit-confirm-password">Confirm New Password</label>
                            <input id="edit-confirm-password" type="password" value={confirmPassword} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.currentTarget.value)} />
                        </div>
                        {error && <p className="login-error">{error}</p>}
                    </form>
                </div>
                <div className="modal-footer">
                    <button onClick={handleSave} className="btn-primary">Save Changes</button>
                </div>
            </div>
        </div>
    );
};

const UserPermissionsModal = ({ isOpen, onClose, user, onSave, allClerks }: { isOpen: boolean, onClose: () => void, user: User | null, onSave: (id: number, permissions: Permission[]) => void, allClerks: string[] }) => {
    const [permissions, setPermissions] = useState<Record<string, any>>({});
    
    useEffect(() => {
        if (isOpen) {
            const count = parseInt(document.body.dataset.modalOpenCount || '0', 10);
            if (count === 0) {
                document.body.classList.add('modal-open');
            }
            document.body.dataset.modalOpenCount = String(count + 1);

            return () => {
                const count = parseInt(document.body.dataset.modalOpenCount || '0', 10);
                const newCount = Math.max(0, count - 1);
                if (newCount === 0) {
                    document.body.classList.remove('modal-open');
                }
                document.body.dataset.modalOpenCount = String(newCount);
            };
        }
    }, [isOpen]);
    
    useEffect(() => {
        if (isOpen && user) {
            api.get<Permission[]>(`/api/users/${user.id}/permissions`).then(perms => {
                const permsMap = perms.reduce((acc, p) => {
                    const value = p.value;
                    if (value && (value.startsWith('[') || value.startsWith('{'))) {
                        try { acc[p.permission] = JSON.parse(value); } catch { acc[p.permission] = value; }
                    } else if (value === 'true' || value === 'false') {
                        acc[p.permission] = value === 'true';
                    }
                    else {
                        acc[p.permission] = value;
                    }
                    return acc;
                }, {} as Record<string, any>);
                
                if (!Array.isArray(permsMap.allowed_clerks)) {
                    permsMap.allowed_clerks = [];
                }
                
                setPermissions(permsMap);
            });
        } else {
            setPermissions({});
        }
    }, [isOpen, user]);

    const handlePermissionChange = (key: string, value: any) => {
        setPermissions(prev => ({ ...prev, [key]: value }));
    };
    
    const handleClerkPermissionChange = (clerk: string, isChecked: boolean) => {
        const currentClerks = permissions.allowed_clerks || [];
        const newClerks = isChecked
            ? [...currentClerks, clerk]
            : currentClerks.filter((c: string) => c !== clerk);
        handlePermissionChange('allowed_clerks', newClerks);
    };
    
    const handleSelectAllClerks = (isChecked: boolean) => {
         handlePermissionChange('allowed_clerks', isChecked ? allClerks : []);
    };
    
    const handleSave = () => {
        if (!user) return;
        const permsToSave: Permission[] = Object.entries(permissions).map(([key, value]) => ({
            permission: key,
            value: typeof value === 'object' ? JSON.stringify(value) : String(value)
        }));
        onSave(user.id, permsToSave);
    };
    
    if (!isOpen || !user) return null;

    const allowedClerks = permissions.allowed_clerks || [];
    const allClerksSelected = allClerks.length > 0 && allowedClerks.length === allClerks.length;

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal" style={{ width: '600px' }} onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <strong>Edit Permissions for {user.name}</strong>
                    <button onClick={onClose}>&times;</button>
                </div>
                <div className="modal-body permissions">
                    <div className="permissions-editor">
                        <div className="permission-group">
                            <h4>General Permissions</h4>
                            <div className="permission-item">
                                <input type="checkbox" id="perm-settings" checked={!!permissions.can_access_settings} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePermissionChange('can_access_settings', e.currentTarget.checked)} />
                                <label htmlFor="perm-settings">Can Manage Clerk PO Series</label>
                            </div>
                            <div className="permission-item">
                                <input type="checkbox" id="perm-user-mgmt" checked={!!permissions.can_access_user_management} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePermissionChange('can_access_user_management', e.currentTarget.checked)} />
                                <label htmlFor="perm-user-mgmt">Can Access User Management</label>
                            </div>
                            <div className="permission-item">
                                <input type="checkbox" id="perm-inventory" checked={!!permissions.can_access_inventory_lookup} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePermissionChange('can_access_inventory_lookup', e.currentTarget.checked)} />
                                <label htmlFor="perm-inventory">Can Access Inventory Lookup</label>
                            </div>
                            <div className="permission-item">
                                <input type="checkbox" id="perm-tracker" checked={!!permissions.can_access_tracker} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePermissionChange('can_access_tracker', e.currentTarget.checked)} />
                                <label htmlFor="perm-tracker">Can Access HH Invoice Tracker</label>
                            </div>
                             <div className="permission-item">
                                <input type="checkbox" id="perm-costs" checked={!!permissions.can_view_costs} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePermissionChange('can_view_costs', e.currentTarget.checked)} />
                                <label htmlFor="perm-costs">Can View Costs</label>
                            </div>
                            <div className="permission-item">
                                <input type="checkbox" id="perm-edit-invoices" checked={!!permissions.can_edit_invoices} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePermissionChange('can_edit_invoices', e.currentTarget.checked)} />
                                <label htmlFor="perm-edit-invoices">Can Edit Invoices</label>
                            </div>
                        </div>
                        <div className="permission-group">
                            <h4>Tracker & Reports Settings</h4>
                            <div className="form-group">
                                <label htmlFor="default-clerk-select">Default Clerk Filter</label>
                                <select
                                    id="default-clerk-select"
                                    value={permissions.default_clerk || 'All'}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handlePermissionChange('default_clerk', e.target.value)}
                                >
                                    <option value="All">All Clerks</option>
                                    {allClerks.map(clerk => (
                                        <option key={clerk} value={clerk}>{clerk}</option>
                                    ))}
                                     <option value="Unknown">?</option>
                                </select>
                            </div>
                            <div className="permission-item">
                                <input type="checkbox" id="perm-unknown-clerk" checked={!!permissions.can_view_unknown_clerk} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePermissionChange('can_view_unknown_clerk', e.currentTarget.checked)} />
                                <label htmlFor="perm-unknown-clerk">Can View Unknown Clerk (?)</label>
                            </div>
                        </div>
                        <div className="permission-group">
                             <div className="clerk-permissions-header">
                                <h4>Clerk Permissions</h4>
                                <div className="clerk-item">
                                    <input type="checkbox" id="clerk-select-all" checked={allClerksSelected} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSelectAllClerks(e.currentTarget.checked)} />
                                    <label htmlFor="clerk-select-all">Select All</label>
                                </div>
                            </div>
                             <p style={{ color: 'var(--text-muted-color)', marginTop: '-0.5rem', marginBottom: '1rem', fontSize: '0.9rem' }}>
                                Select which clerks' outstanding invoices this user can view in the tracker.
                            </p>
                            <div className="clerk-list">
                                {allClerks.map(clerk => (
                                    <div key={clerk} className="clerk-item">
                                        <input type="checkbox" id={`clerk-${clerk}`} checked={allowedClerks.includes(clerk)} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleClerkPermissionChange(clerk, e.currentTarget.checked)}/>
                                        <label htmlFor={`clerk-${clerk}`}>{clerk}</label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="modal-footer">
                    <button onClick={handleSave} className="btn-primary">Save Changes</button>
                </div>
            </div>
        </div>
    );
};

const ClerkPoRangesEditor = () => {
    const [clerkPoRanges, setClerkPoRanges] = useState<Record<string, number[]>>({});
    const [editedRanges, setEditedRanges] = useState<Record<string, number[]>>({});
    const [selectedClerk, setSelectedClerk] = useState<string | null>(null);
    const [newSeriesInput, setNewSeriesInput] = useState('');
    const [newClerkInitials, setNewClerkInitials] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    const isDirty = useMemo(() => JSON.stringify(clerkPoRanges) !== JSON.stringify(editedRanges), [clerkPoRanges, editedRanges]);

    const fetchRanges = useCallback(async () => {
        setIsLoading(true);
        try {
            const ranges = await api.get<Record<string, number[]>>('/api/clerk-po-ranges');
            setClerkPoRanges(ranges);
            setEditedRanges(JSON.parse(JSON.stringify(ranges)));
        } catch (error) {
            window.alert("Failed to load PO ranges.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRanges();
    }, [fetchRanges]);

    const handleAddClerk = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const clerk = newClerkInitials.trim();
        if (clerk && !editedRanges.hasOwnProperty(clerk)) {
            setEditedRanges(prev => ({ ...prev, [clerk]: [] }));
            setNewClerkInitials('');
            setSelectedClerk(clerk);
        } else if (editedRanges.hasOwnProperty(clerk)) {
            window.alert(`Clerk "${clerk}" already exists.`);
        }
    };

    const handleRemoveClerk = (clerkToRemove: string) => {
        if (window.confirm(`Are you sure you want to remove clerk "${clerkToRemove}" and all their assigned series?`)) {
            const { [clerkToRemove]: _, ...rest } = editedRanges;
            setEditedRanges(rest);
            if (selectedClerk === clerkToRemove) {
                setSelectedClerk(null);
            }
        }
    };
    
    const parseSeries = (input: string): number | null => {
        const cleaned = input.replace(/[^0-9]/g, '');
        if (/^\d{8,9}$/.test(cleaned)) {
            return parseInt(cleaned, 10);
        }
        return null;
    };
    
    const formatSeries = (prefix: number): string => {
        const s = String(prefix).padStart(8, '0');
        return `${s.slice(0, 2)}-${s.slice(2)}`;
    };

    const handleAddSeries = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!selectedClerk) return;
        const newSeriesPrefix = parseSeries(newSeriesInput);

        if (newSeriesPrefix !== null) {
            let existingClerk: string | null = null;
            for (const [clerk, series] of Object.entries(editedRanges)) {
                if (series.includes(newSeriesPrefix)) {
                    existingClerk = clerk;
                    break;
                }
            }

            if (existingClerk) {
                 window.alert(`Series "${formatSeries(newSeriesPrefix)}" is already assigned to clerk "${existingClerk}".`);
            } else {
                const clerkSeries = editedRanges[selectedClerk] || [];
                const updatedSeries = [...clerkSeries, newSeriesPrefix].sort((a, b) => a - b);
                setEditedRanges(prev => ({ ...prev, [selectedClerk]: updatedSeries }));
                setNewSeriesInput('');
            }
        } else {
            window.alert(`Invalid series format. Please enter a PO series with 6 or 7 digits after the dash, like 24-123401 or 24-1234501.`);
        }
    };

    const handleRemoveSeries = (seriesToRemove: number) => {
        if (!selectedClerk) return;
        const updatedSeries = editedRanges[selectedClerk].filter(s => s !== seriesToRemove);
        setEditedRanges(prev => ({ ...prev, [selectedClerk]: updatedSeries }));
    };

    const handleDiscardChanges = () => {
        if (window.confirm("Are you sure you want to discard all changes?")) {
            setEditedRanges(JSON.parse(JSON.stringify(clerkPoRanges)));
            setSelectedClerk(null);
        }
    };

    const handleSaveChanges = async () => {
        setIsLoading(true);
        try {
            await api.post('/api/clerk-po-ranges', editedRanges);
            window.alert('Clerk PO ranges updated successfully.');
            await fetchRanges();
        } catch (error) {
            window.alert(`Error updating clerk PO ranges: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsLoading(false);
        }
    };

    const sortedClerks = Object.keys(editedRanges).sort();

    return (
        <div className="settings-card">
            <h3>Clerk PO Series</h3>
            <p className="settings-card-description">Assign PO series to clerks. Each series is a block of 100 (e.g., 24-123401 covers from 24-123401 to 24-123500; 24-1234501 covers from 24-1234501 to 24-1234599).</p>
            <div className="settings-po-layout">
                <aside className="clerk-list-sidebar">
                    <h4>Clerks</h4>
                    <form className="add-clerk-form" onSubmit={handleAddClerk}>
                        <input
                            type="text"
                            placeholder="New Clerk Initials"
                            value={newClerkInitials}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewClerkInitials(e.currentTarget.value.toUpperCase())}
                            maxLength={10}
                        />
                        <button type="submit" title="Add New Clerk">+</button>
                    </form>
                    <ul className="clerk-list">
                        {sortedClerks.map(clerk => {
                             const clerkIsDirty = JSON.stringify(editedRanges[clerk]) !== JSON.stringify(clerkPoRanges[clerk] || []);
                            return (
                                <li key={clerk}>
                                    <button
                                        className={`clerk-list-item ${selectedClerk === clerk ? 'active' : ''}`}
                                        onClick={() => setSelectedClerk(clerk)}
                                    >
                                        <div className="clerk-name-indicator">
                                            {clerk}
                                            {clerkIsDirty && <div className="dirty-indicator" title="Unsaved changes"></div>}
                                        </div>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </aside>
                <main className="clerk-editor-pane">
                    {selectedClerk ? (
                        <>
                            <h4>Editing Series for <strong style={{color: 'var(--primary-color)'}}>{selectedClerk}</strong></h4>
                            <div className="series-chips-container">
                                {editedRanges[selectedClerk]?.length > 0 ? [...editedRanges[selectedClerk]].sort((a, b) => a - b).map(series => (
                                    <span key={series} className="series-tag">
                                        {formatSeries(series)}
                                        <button onClick={() => handleRemoveSeries(series)} title={`Remove series ${formatSeries(series)}`}>&times;</button>
                                    </span>
                                )) : <span style={{color: 'var(--text-muted-color)'}}>No series assigned.</span>}
                            </div>
                            <form onSubmit={handleAddSeries} className="add-series-form">
                                <input
                                    type="text"
                                    placeholder="Add series (e.g., 24-123401 or 24-1234501)"
                                    value={newSeriesInput}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewSeriesInput(e.currentTarget.value)}
                                />
                                <button type="submit">Add</button>
                            </form>
                            <div className="editor-actions">
                                <button onClick={() => handleRemoveClerk(selectedClerk)} className="remove-clerk-btn">Remove Clerk</button>
                            </div>
                        </>
                    ) : (
                         <div className="placeholder">
                            <p>Select a clerk to edit their PO series.</p>
                        </div>
                    )}
                </main>
            </div>
             <div className="settings-card-footer">
                <button className="btn-secondary" onClick={handleDiscardChanges} disabled={!isDirty || isLoading}>Discard Changes</button>
                <button className="btn-primary" onClick={handleSaveChanges} disabled={!isDirty || isLoading}>
                    {isLoading ? 'Saving...' : 'Save Changes'}
                </button>
            </div>
        </div>
    );
};

export const Settings = ({ user: currentUser, onPermissionsChange }: { user: User; onPermissionsChange: () => void; }) => {
    const [users, setUsers] = useState<User[]>([]);
    const [editingUserDetails, setEditingUserDetails] = useState<User | null>(null);
    const [editingUserPermissions, setEditingUserPermissions] = useState<User | null>(null);
    const [clerkPoRanges, setClerkPoRanges] = useState<Record<string, number[]>>({});
    const [isLoading, setIsLoading] = useState(true);

    const allClerks = useMemo(() => Object.keys(clerkPoRanges).sort(), [clerkPoRanges]);
    
    const canManageClerkPo = currentUser.permissions?.can_access_settings === 'true';
    const canManageUsers = currentUser.permissions?.can_access_user_management === 'true';

    const fetchAllData = useCallback(async () => {
        setIsLoading(true);
        try {
            const promises: Promise<any>[] = [];
            if (canManageUsers) promises.push(api.get<User[]>('/api/users'));
            else promises.push(Promise.resolve([]));
            
            promises.push(api.get<Record<string, number[]>>('/api/clerk-po-ranges'));

            const [fetchedUsers, fetchedRanges] = await Promise.all(promises);

            setUsers(fetchedUsers);
            setClerkPoRanges(fetchedRanges);
        } catch (error) {
            // No-op
        } finally {
            setIsLoading(false);
        }
    }, [canManageUsers]);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    const handleSaveUserDetails = async (userId: number, data: { name: string, username: string, password?: string }) => {
        try {
            await api.put(`/api/users/${userId}`, data);
            setEditingUserDetails(null);
            fetchAllData();
            window.alert('User details updated successfully.');
        } catch (error) {
            window.alert(`Error updating user: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };
    
    const handleSaveUserPermissions = async (userId: number, permissions: Permission[]) => {
        try {
            await api.put(`/api/users/${userId}/permissions`, permissions);
            setEditingUserPermissions(null);
            window.alert('Permissions updated successfully.');
            if (userId === currentUser.id) {
                onPermissionsChange();
            }
        } catch (error) {
            window.alert('Error updating permissions.');
        }
    };
    
    const handleAddUser = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const form = e.currentTarget;
        const name = (form.elements.namedItem('name') as HTMLInputElement).value;
        const username = (form.elements.namedItem('username') as HTMLInputElement).value;
        const password = (form.elements.namedItem('password') as HTMLInputElement).value;
        if (!username || !password || !name) return window.alert('First name, username and password are required.');

        try {
            await api.post('/api/users', { name, username, password });
            form.reset();
            fetchAllData();
            window.alert('User created successfully.');
        } catch (error) {
            window.alert(`Error creating user: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };
    
    const handleDeleteUser = async (userId: number) => {
        if (window.confirm('Are you sure you want to delete this user? This cannot be undone.')) {
            try {
                await api.delete(`/api/users/${userId}`);
                fetchAllData();
                window.alert('User deleted successfully.');
            } catch (error) {
                window.alert(`Error deleting user: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
        }
    };

    const handleForcePasswordChange = async (userId: number, userName: string) => {
        if (window.confirm(`Are you sure you want to force a password change for ${userName}?`)) {
            try {
                await api.patch(`/api/users/${userId}/force-password-change`, { force: true });
                window.alert(`${userName} will be required to change their password on next login.`);
                fetchAllData();
            } catch (error) {
                window.alert(`Error forcing password change: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
        }
    };


    if (isLoading && users.length === 0 && Object.keys(clerkPoRanges).length === 0) {
        return <p>Loading settings...</p>;
    }

    return (
        <div className="settings-container">
            <h2>Settings</h2>
            <p className="settings-description">Manage application settings, user permissions, and clerk PO assignments.</p>
            
            {canManageClerkPo && <ClerkPoRangesEditor />}

            {canManageUsers && (
                <div className="settings-card">
                    <h3>User Management</h3>
                    <p className="settings-card-description">Add, remove, and manage permissions for users.</p>
                    <table className="settings-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Username</th>
                                <th className="actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(user => (
                                <tr key={user.id}>
                                    <td>
                                        {user.name}
                                        {user.force_password_change && <span style={{ color: 'var(--credit-color)', fontSize: '0.8rem', marginLeft: '0.5rem', fontWeight: 'bold' }}>(Pending Reset)</span>}
                                    </td>
                                    <td>{user.username}</td>
                                    <td className="actions">
                                        <button onClick={() => setEditingUserDetails(user)}>Edit</button>
                                        <button onClick={() => setEditingUserPermissions(user)}>Permissions</button>
                                        <button
                                            onClick={() => handleForcePasswordChange(user.id, user.name)}
                                            disabled={user.id === currentUser.id || !!user.force_password_change}
                                            title={
                                                user.id === currentUser.id ? "You cannot force your own password change."
                                                : user.force_password_change ? "Password change is already pending."
                                                : "Force password change on next login"
                                            }
                                        >
                                            Force Pwd Change
                                        </button>
                                        <button 
                                            className="delete" 
                                            onClick={() => handleDeleteUser(user.id)}
                                            disabled={user.id === currentUser.id}
                                            title={user.id === currentUser.id ? "You cannot delete your own account." : ""}
                                        >Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border-color)' }}>
                        <h4 style={{ marginBottom: '1rem', fontSize: '1.1rem', fontWeight: 600 }}>Add New User</h4>
                        <form className="settings-form" onSubmit={handleAddUser} style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
                            <div className="form-group">
                                <label htmlFor="new-name">First Name</label>
                                <input id="new-name" name="name" type="text" required />
                            </div>
                            <div className="form-group">
                                <label htmlFor="new-username">Username</label>
                                <input id="new-username" name="username" type="text" required />
                            </div>
                            <div className="form-group">
                                <label htmlFor="new-password">Password</label>
                                <input id="new-password" name="password" type="password" required />
                            </div>
                            <button type="submit" className="btn-primary" style={{ padding: '0.5rem 1rem' }}>Add User</button>
                        </form>
                    </div>
                </div>
            )}

            <UserDetailModal isOpen={!!editingUserDetails} onClose={() => setEditingUserDetails(null)} user={editingUserDetails} onSave={handleSaveUserDetails} />
            <UserPermissionsModal isOpen={!!editingUserPermissions} onClose={() => setEditingUserPermissions(null)} user={editingUserPermissions} onSave={handleSaveUserPermissions} allClerks={allClerks} />
        </div>
    );
};
